import type { Icon } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import type { ReactNode } from "react";
import { cx } from "@/lib/cx";
import { Skeleton } from "./Skeleton";

/**
 * One number with a label, optionally a link into the list it counts. `value` undefined renders a
 * skeleton (cold load); `hint` is a muted line under the number.
 */
export function StatTile({
  label,
  value,
  hint,
  icon: I,
  to,
}: {
  label: string;
  value: ReactNode | undefined;
  hint?: ReactNode;
  icon?: Icon;
  /** Admin route this tile opens. */
  to?: "/admin/users" | "/admin/invites" | "/admin/groups" | "/admin/roles";
}) {
  const body = (
    <>
      <span className="flex items-center gap-1.5 text-xs font-medium text-fg-muted">
        {I ? <I size={14} aria-hidden="true" className="text-fg-subtle" /> : null}
        {label}
      </span>
      <span className="mt-2 text-2xl font-semibold tracking-tight text-fg tnum">
        {value === undefined ? <Skeleton className="mt-1 h-6 w-10" /> : value}
      </span>
      {hint ? <span className="mt-0.5 text-xs text-fg-muted">{hint}</span> : null}
    </>
  );
  const box =
    "flex min-w-0 flex-col rounded-panel border border-line bg-surface p-4 shadow-[0_1px_2px_var(--shadow-soft)]";
  return to ? (
    <Link to={to} className={cx(box, "transition-ui hover:border-line-strong hover:bg-hover")}>
      {body}
    </Link>
  ) : (
    <div className={box}>{body}</div>
  );
}
